/**
 * TowerPanel.tsx — W66.X6 REBUILD: Command View — Detroit-skyline tower
 *
 * Renders the Detroit skyline WEBP image with floor pip overlays per Bryan K. Short
 * canonical money-shot spec (locked 2026-05-30 / confirmed 2026-06-02).
 *
 * Image: /hero/mi6-tower-clean-desktop.webp (864×1821).
 * FLOOR_Y_PCT: pixel-detected — do not change.
 *
 * Floor pip states (canonical 5-color palette only):
 *   Completed (answeredCps.has(n) && n < activeFloor): #16A34A green fill + 6px glow
 *   Current (n === activeFloor): #DC2626 red fill + 10px glow + cv-floor-pulse animation
 *   Future (n > activeFloor): transparent fill + 1px #6B7280 gray ring
 *   Selected (selectedCp === n): override border #EAB308 yellow + 12px glow
 *
 * W45.3: wired to SelectedCpContext. Clicking any floor pip calls setSelectedCp.
 *
 * Brand: No exclamation points. No emojis. M-PACT hyphenated.
 * "Checkpoint" not "Gate". AE name is Demo AE 1 — never Geoff.
 * 5-color palette: #6B7280 gray, #DC2626 red, #EA580C orange, #EAB308 yellow, #16A34A green.
 */

import React from "react";
import { useSelectedCp } from "@/hooks/useSelectedCp";
import { HELIX_CHECKPOINTS } from "@/lib/helix-checkpoints";

// ─── Canonical 5-color palette (locked) ──────────────────────────────────────
const COLOR_COMPLETED = "#16A34A"; // green — cleared checkpoint
const COLOR_CURRENT   = "#DC2626"; // red   — active checkpoint
const COLOR_FUTURE    = "#6B7280"; // gray  — not yet reached
const COLOR_SELECTED  = "#EAB308"; // yellow — cross-panel selection

// ─── Pixel-detected floor Y positions (% of image height) ────────────────────
// Floor 1 = ground level, floor 10 = penthouse. DO NOT CHANGE.
const FLOOR_Y_PCT: Record<number, number> = {
  1: 58.76, 2: 53.71, 3: 48.71, 4: 43.71, 5: 38.71,
  6: 33.77, 7: 28.83, 8: 23.89, 9: 18.95, 10: 14.06,
};

// ─── Description card ────────────────────────────────────────────────────────

function TowerDescriptionCard() {
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E7EB",
        borderRadius: 8,
        padding: 12,
        flexShrink: 0,
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "#111827",
          marginBottom: 4,
        }}
      >
        Pipeline Tower
      </div>
      <div
        style={{
          fontSize: 11,
          color: "#6B7280",
          lineHeight: 1.55,
        }}
      >
        The architectural visualization of where this deal sits in the approval
        ladder. Floors light green as each checkpoint is cleared. The current
        floor pulses red. Tap a floor to focus that checkpoint across all three
        panels.
      </div>
    </div>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface TowerPanelProps {
  accountId: string;
  accountName?: string;
  /** Latest CP number the account has work recorded for (1-10). Null = no data. */
  currentCp: number | null;
  /** Set of CP numbers (1-10) that have at least one answer recorded. */
  answeredCps: Set<number>;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function TowerPanel({
  accountName,
  currentCp,
  answeredCps,
}: TowerPanelProps) {
  const activeFloor = currentCp ?? 1;
  const { selectedCp, setSelectedCp } = useSelectedCp();

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 320,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Panel header ── */}
      <div
        style={{
          padding: "10px 14px 8px",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#6B7280",
          }}
        >
          Process Elevation
        </div>
        {accountName && (
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "rgba(250,250,247,0.9)",
              marginTop: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {accountName}
          </div>
        )}
      </div>

      {/* ── Two-row block below header ── */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          padding: "8px 10px",
          gap: 8,
        }}
      >
        {/* Row 1: description card (~88px) */}
        <TowerDescriptionCard />

        {/* Row 2: tower image with pip overlays (flex 1) */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            position: "relative",
          }}
        >
          {/* Detroit skyline tower image */}
          <img
            src="/hero/mi6-tower-clean-desktop.webp"
            alt="Detroit-skyline tower — CP1 base to CP10 roof"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              objectPosition: "center top",
              pointerEvents: "none",
              userSelect: "none",
            }}
          />

          {/* Floor pip overlays — floors 1..10 */}
          {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
            const cp = HELIX_CHECKPOINTS[n - 1];
            const cpName = cp ? cp.name : `CP${n}`;
            const truncatedName = cpName.length > 14 ? cpName.slice(0, 14) + "…" : cpName;

            const isCompleted = answeredCps.has(n) && n < activeFloor;
            const isCurrent   = n === activeFloor;
            const isSelected  = selectedCp === n;

            // Pip fill / border / glow / animation
            let pipFill: string;
            let pipBorder: string;
            let pipGlow: string;
            let pipAnimation: string;

            if (isCompleted) {
              pipFill      = COLOR_COMPLETED;
              pipBorder    = COLOR_COMPLETED;
              pipGlow      = `0 0 6px ${COLOR_COMPLETED}99`;
              pipAnimation = "none";
            } else if (isCurrent) {
              pipFill      = COLOR_CURRENT;
              pipBorder    = COLOR_CURRENT;
              pipGlow      = `0 0 10px ${COLOR_CURRENT}99`;
              pipAnimation = "cv-floor-pulse 2.4s ease-in-out infinite";
            } else {
              // future
              pipFill      = "transparent";
              pipBorder    = COLOR_FUTURE;
              pipGlow      = "none";
              pipAnimation = "none";
            }

            // Selected override
            if (isSelected) {
              pipBorder = COLOR_SELECTED;
              pipGlow   = `0 0 12px ${COLOR_SELECTED}99`;
            }

            const labelColor = isSelected ? COLOR_SELECTED : pipBorder;
            const yPct = FLOOR_Y_PCT[n];

            return (
              <React.Fragment key={n}>
                {/* Invisible click hit area — 22px tall, full panel width */}
                <button
                  type="button"
                  aria-label={`Checkpoint ${n} — ${cpName}`}
                  onClick={() => setSelectedCp(selectedCp === n ? null : n)}
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: `calc(${yPct}% - 11px)`,
                    height: 22,
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    zIndex: 20,
                    padding: 0,
                    margin: 0,
                    outline: "none",
                  }}
                />

                {/* Pip circle (12px) */}
                <div
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: `${yPct}%`,
                    transform: "translate(-50%, -50%)",
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: pipFill,
                    border: `1px solid ${pipBorder}`,
                    boxShadow: pipGlow,
                    animation: pipAnimation,
                    pointerEvents: "none",
                    zIndex: 10,
                    transition: "border-color 0.25s ease, box-shadow 0.25s ease",
                  }}
                />

                {/* CP label: "CP{n} {truncated name}" right of pip */}
                <div
                  style={{
                    position: "absolute",
                    top: `${yPct}%`,
                    left: "calc(50% + 16px)",
                    transform: "translateY(-50%)",
                    fontSize: 10,
                    fontWeight: 600,
                    color: labelColor,
                    whiteSpace: "nowrap",
                    fontVariantNumeric: "tabular-nums",
                    pointerEvents: "none",
                    zIndex: 10,
                    letterSpacing: "0.02em",
                    transition: "color 0.25s ease",
                  }}
                >
                  CP{n} {truncatedName}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes cv-floor-pulse {
          0%, 100% { box-shadow: 0 0 6px ${COLOR_CURRENT}66; }
          50%       { box-shadow: 0 0 14px ${COLOR_CURRENT}CC; }
        }
      `}</style>
    </div>
  );
}
